import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import cn from 'classnames';
import FocusContainer from '../Helpers/FocusContainer';
import ResizeObserver from '../Helpers/ResizeObserver';
import Paper from '../Papers/Paper';
import DialogTitle from './DialogTitle';
import DialogFooter from './DialogFooter';

const DIFF_KEYS = ['style', 'height', 'width', 'contentStyle'];

/**
 * The `Dialog` is just a static component for creating dialogs. Dialogs
 * seemed like they could be used outside of the `DialogContainer` component,
 * so it was exposed as well. In *most* cases, you will still want to use
 * the `DialogContainer` component.
 */
export default class Dialog extends PureComponent {
  static defaultProps = {
    autopadContent: true,
    autosizeContent: true,
    contentComponent: 'section',
    zDepth: 5,
  };

  constructor(props) {
    super();

    const { height, width } = props;
    let styles = props.style;
    if (height || width) {
      styles = styles || {};
      styles = { height, width, ...styles };
    }

    this.state = {
      styles,
      contentStyles: props.contentStyle,
      contentPadded: false,
    };
  }

  componentWillMount() {
    const { pageX, pageY } = this.props;
    if (!pageX || !pageY) {
      return;
    }

    this.setState({ styles: this._getStyles(this.props) });
  }

  componentDidMount() {
    if (this.props.onOpen) {
      this.props.onOpen();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (DIFF_KEYS.some(key => nextProps[key] !== this.props[key])) {
      this.setState({
        styles: this._getStyles(nextProps),
        contentStyles: { ...this.state.contentStyles, ...nextProps.contentStyle },
      });
    }
  }

  componentWillUnmount() {
    if (this.props.onLeave) {
      this.props.onLeave();
    }
  }

  _getStyles = ({ pageX, containerX, pageY, containerY, height, width, style } = this.props) => ({
    height: typeof height !== 'undefined' ? height : null,
    width: typeof width !== 'undefined' ? width : null,
    transformOrigin: pageX || pageY ? `${pageX - containerX}px ${pageY - containerY}px` : null,
    ...style,
  });

  _setRenderNode = (dialog) => {
    this._renderNode = findDOMNode(dialog);
  };

  _setContent = (content) => {
    if (content !== null) {
      this._content = findDOMNode(content);
      const contentPadded = this._content.querySelectorAll('.md-list').length === 0;

      this.setState({ contentPadded });
    }
  };

  _handleContentResize = ({ scrollHeight, el: content }) => {
    const maxHeight = content.style.maxHeight;
    const dialog = content.parentNode;
    content.style.maxHeight = 'none';
    const title = this.props.title ? dialog.querySelector('.md-title--dialog') : null;
    const footer = this.props.actions ? dialog.querySelector('.md-dialog-footer') : null;

    const totalHeight = dialog.offsetHeight - (title ? title.offsetHeight : 0) - (footer ? footer.offsetHeight : 0);
    content.style.maxHeight = maxHeight;
    const equalHeight = totalHeight === scrollHeight;
    if (equalHeight) {
      const currentHeight = (this.state.contentStyles && this.state.contentStyles.maxHeight) || null;
      if (currentHeight && currentHeight !== scrollHeight) {
        this.setState({ contentStyles: this.props.contentStyle });
      }
    } else {
      this.setState({ contentStyles: { maxHeight: totalHeight, ...this.props.contentStyle } });
    }
  };

  render() {
    const { contentPadded, styles, contentStyles } = this.state;
    const {
      id,
      className,
      titleStyle,
      titleClassName,
      footerStyle,
      footerClassName,
      contentClassName,
      title,
      contentComponent: Content,
      contentProps,
      actions,
      children,
      fullPage,
      centered,
      autopadContent,
      paddedContent,
      autosizeContent,
      stackedActions,
      /* eslint-disable no-unused-vars */
      style,
      contentStyle,
      pageX,
      pageY,
      containerX,
      containerY,
      onOpen,
      onLeave,
      height,
      width,
      /* eslint-enable no-unused-vars */
      ...props
    } = this.props;

    let { 'aria-labelledby': labelledBy } = this.props;
    const titleId = `${id}-title`;
    if (!labelledBy && title) {
      labelledBy = titleId;
    }

    const padDefined = typeof paddedContent !== 'undefined';
    const dialogChildren = fullPage ? children : [
      <DialogTitle
        key="title"
        id={titleId}
        style={titleStyle}
        className={titleClassName}
      >
        {title}
      </DialogTitle>,
      <Content
        ref={!padDefined && autopadContent ? this._setContent : null}
        key="content"
        {...contentProps}
        style={contentStyles}
        className={cn('md-dialog-content', {
          'md-dialog-content--padded': padDefined ? paddedContent : contentPadded,
        }, contentClassName)}
      >
        {autosizeContent ? <ResizeObserver watchHeight watchWidth onResize={this._handleContentResize} /> : null}
        {children}
      </Content>,
      <DialogFooter
        key="footer"
        style={footerStyle}
        className={footerClassName}
        actions={actions}
        stacked={stackedActions}
      />,
    ];

    return (
      <Paper
        {...props}
        id={id}
        component={FocusContainer}
        ref={this._setRenderNode}
        style={styles}
        className={cn('md-dialog', {
          'md-dialog--full-page': fullPage,
          'md-dialog--centered': centered,
        }, className)}
        role="dialog"
        aria-labelledby={labelledBy}
      >
        {dialogChildren}
      </Paper>
    );
  }
}
